import { API_BASE_URL } from '../config/api';
import { authService } from './authService';

export type RecordingUploadTarget = 'audio' | 'video' | 'animation';
export type RecordingSourceType = 'microphone' | 'camera' | 'upload';

export interface RecordingUploadResult {
  mediaType: RecordingUploadTarget;
  sourceType: RecordingSourceType;
  url: string;
  publicId?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  recordingId?: number;
  previewImageUrl?: string;
  previewImagePublicId?: string;
  storageProvider?: string;
}

interface UploadRecordingInput {
  endpoint: RecordingUploadTarget;
  file: File;
  sourceType: RecordingSourceType;
  fallbackUserId?: string;
  title?: string;
  description?: string;
  previewImageFile?: File | null;
}

const normalizeUserIdHeader = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const digits = value.match(/\d+/g)?.join('') ?? '';
  return digits ? digits.slice(0, 12) : undefined;
};

const getRequestHeaders = (fallbackUserId?: string): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: '*/*',
  };
  const token = authService.getToken();
  const storedUser = authService.getStoredUser();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const userIdHeader = normalizeUserIdHeader(storedUser?.id) ?? normalizeUserIdHeader(fallbackUserId);
  if (!token && userIdHeader) {
    headers['x-user-id'] = userIdHeader;
  }

  return headers;
};

const parseResponse = async (response: Response): Promise<Record<string, unknown>> => {
  let payload: Record<string, unknown> = {};

  try {
    const data = await response.json();
    payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const errorRecord =
      payload.error && typeof payload.error === 'object' ? (payload.error as Record<string, unknown>) : {};
    const message =
      (typeof errorRecord.message === 'string' && errorRecord.message) ||
      (typeof payload.message === 'string' && payload.message) ||
      (typeof payload.error === 'string' && payload.error) ||
      `Upload failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload;
};

export const mediaRecordingService = {
  async uploadRecording({
    endpoint,
    file,
    sourceType,
    fallbackUserId,
    title,
    description,
    previewImageFile,
  }: UploadRecordingInput): Promise<RecordingUploadResult> {
    const formData = new FormData();
    formData.append('recording', file);
    formData.append('sourceType', sourceType);
    if (title?.trim()) {
      formData.append('title', title.trim());
    }
    if (description?.trim()) {
      formData.append('description', description.trim());
    }
    if (previewImageFile) {
      formData.append('previewImage', previewImageFile);
    }

    const response = await fetch(`${API_BASE_URL}/media/recordings/${endpoint}`, {
      method: 'POST',
      headers: getRequestHeaders(fallbackUserId),
      body: formData,
    });

    const payload = await parseResponse(response);
    const dataRecord =
      payload.data && typeof payload.data === 'object' ? (payload.data as Record<string, unknown>) : payload;
    const recordingRecord =
      dataRecord.recording && typeof dataRecord.recording === 'object'
        ? (dataRecord.recording as Record<string, unknown>)
        : {};
    const fileRecord =
      dataRecord.file && typeof dataRecord.file === 'object'
        ? (dataRecord.file as Record<string, unknown>)
        : {};
    const previewImageRecord =
      dataRecord.previewImage && typeof dataRecord.previewImage === 'object'
        ? (dataRecord.previewImage as Record<string, unknown>)
        : {};

    return {
      mediaType: endpoint,
      sourceType,
      url: typeof fileRecord.url === 'string' ? fileRecord.url : '',
      publicId: typeof fileRecord.publicId === 'string' ? fileRecord.publicId : undefined,
      originalName: typeof fileRecord.originalName === 'string' ? fileRecord.originalName : undefined,
      mimeType: typeof fileRecord.mimeType === 'string' ? fileRecord.mimeType : undefined,
      size: typeof fileRecord.size === 'number' ? fileRecord.size : undefined,
      recordingId:
        typeof recordingRecord.recordingId === 'number'
          ? recordingRecord.recordingId
          : typeof fileRecord.recordingId === 'number'
            ? fileRecord.recordingId
            : undefined,
      previewImageUrl:
        typeof previewImageRecord.url === 'string'
          ? previewImageRecord.url
          : typeof recordingRecord.previewImageUrl === 'string'
            ? recordingRecord.previewImageUrl
            : undefined,
      previewImagePublicId:
        typeof previewImageRecord.publicId === 'string'
          ? previewImageRecord.publicId
          : typeof recordingRecord.previewImagePublicId === 'string'
            ? recordingRecord.previewImagePublicId
            : undefined,
      storageProvider:
        typeof recordingRecord.storageProvider === 'string' ? recordingRecord.storageProvider : undefined,
    };
  },
};
